import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus, Pencil, Trash2, PackagePlus, AlertTriangle, Package } from 'lucide-react'
import { toast } from 'sonner'
import { useInventory, type InventoryItem } from '@/hooks/use-inventory'
import { INVENTORY_PACKS } from '@/lib/inventory-starter-packs'
import { Page, PageHeader, Card, Table, Trow, btnPrimary, btnSecondary, btnGhost, EmptyState, SkeletonRows } from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { peso } from '@/lib/ph'

export const Route = createFileRoute('/_app/inventory')({ component: InventoryPage })

const money = (n: number) => peso(n)
const INV_COLS = '1.6fr 150px 110px 110px 120px 120px'
const UNITS = ['unit', 'ea', 'L', 'mL', 'kg', 'g', 'm', 'box', 'roll', 'pack', 'hr']

type Draft = Partial<InventoryItem> & { id?: string }
const BLANK: Draft = { name: '', category: '', unit: 'unit', unit_cost: 0, qty_on_hand: 0, reorder_point: 0 }

function InventoryPage() {
  const { items, lowStock, stockValue, isLoading, save, adjust, remove, importMany } = useInventory()
  const [packOpen, setPackOpen] = useState(false)
  const [packId, setPackId] = useState(INVENTORY_PACKS[0].id)

  const applyPack = async () => {
    const pack = INVENTORY_PACKS.find((p) => p.id === packId)
    if (!pack) return
    try {
      await importMany.mutateAsync(pack.items.map((it) => ({
        name: it.name, unit: it.unit, category: it.category, reorder_point: it.reorder,
      })))
      toast.success(`Added ${pack.items.length} ${pack.label} items with reorder points. Do a stocktake to set quantities.`)
      setPackOpen(false)
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const [editing, setEditing] = useState<Draft | null>(null)
  const [restock, setRestock] = useState<{ item: InventoryItem; qty: number } | null>(null)

  const submit = async () => {
    if (!editing?.name?.trim()) return toast.error('Give the item a name')
    try {
      await save.mutateAsync(editing)
      toast.success(editing.id ? 'Item updated' : 'Item added')
      setEditing(null)
    } catch (e) { toast.error((e as Error).message) }
  }

  const doRestock = async () => {
    if (!restock || !restock.qty) return setRestock(null)
    try {
      await adjust.mutateAsync({ id: restock.item.id, delta: restock.qty, reason: 'restock' })
      toast.success(`Restocked ${restock.item.name}`)
      setRestock(null)
    } catch (e) { toast.error((e as Error).message) }
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Stock & materials"
        title="Inventory"
        subtitle="Track stock on hand and what each job uses. Material cost feeds job profit — it never appears on the customer's invoice."
        actions={<><button className={btnSecondary} onClick={() => setPackOpen(true)}><Package className="h-4 w-4" /> Starter pack</button><button className={btnPrimary} onClick={() => setEditing({ ...BLANK })}><Plus className="h-4 w-4" /> New item</button></>}
      />

      {/* summary */}
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:grid-cols-3">
        <Card className="px-4 py-3"><p className="text-[12px] font-semibold uppercase tracking-wide text-token-secondary">Items</p><p className="font-display mt-1 text-[22px] font-bold text-token-primary">{items.length}</p></Card>
        <Card className="px-4 py-3"><p className="text-[12px] font-semibold uppercase tracking-wide text-token-secondary">Stock value</p><p className="font-display mt-1 text-[22px] font-bold text-token-primary tabular-nums">{money(stockValue)}</p></Card>
        <Card className="px-4 py-3"><p className="text-[12px] font-semibold uppercase tracking-wide text-token-secondary">Low stock</p><p className={`font-display mt-1 text-[22px] font-bold tabular-nums ${lowStock.length ? 'text-token-warning' : 'text-token-primary'}`}>{lowStock.length}</p></Card>
      </div>

      {lowStock.length > 0 && (
        <div className="mt-4 flex items-start gap-2.5 rounded-[12px] border border-token-warning bg-token-warning-surface px-4 py-3 text-[13.5px] text-token-warning">
          <AlertTriangle className="mt-0.5 h-[18px] w-[18px] flex-none" />
          <span><strong>{lowStock.length} item{lowStock.length === 1 ? '' : 's'} at or below reorder point:</strong> {lowStock.slice(0, 6).map((i) => i.name).join(', ')}{lowStock.length > 6 ? '…' : ''}. Time to restock.</span>
        </div>
      )}

      <div className="mt-4">
        {isLoading ? (
          <Card><SkeletonRows count={5} /></Card>
        ) : items.length === 0 ? (
          <Card><EmptyState label="No inventory yet. Load a starter pack for your trade, then adjust quantities after a stocktake." cta="Starter pack" onCta={() => setPackOpen(true)} /></Card>
        ) : (
          <Table
            cols={INV_COLS}
            header={<><span>Item</span><span className="text-right">On hand</span><span className="text-right">Reorder at</span><span className="text-right">Unit cost</span><span className="text-right">Value</span><span /></>}
          >
            {items.map((i, idx) => {
              const low = i.reorder_point > 0 && i.qty_on_hand <= i.reorder_point
              return (
                // No onClick: inventory items have no detail page, the row actions are the interaction.
                <Trow key={i.id} cols={INV_COLS} last={idx === items.length - 1}>
                  <span className="min-w-0">
                    <span className="block truncate font-bold text-token-primary">{i.name}</span>
                    <span className="block truncate text-[12px] text-token-secondary">{[i.category, i.sku].filter(Boolean).join(' · ') || '—'}</span>
                  </span>
                  <span className="text-right tabular-nums">
                    <span className={low ? 'font-bold text-token-warning' : 'text-token-primary'}>{i.qty_on_hand}</span>
                    <span className="text-token-secondary"> {i.unit}</span>
                    {low && <span className="ml-1.5 rounded-[5px] bg-token-warning-surface px-1.5 py-0.5 text-[10px] font-bold uppercase text-token-warning">Low</span>}
                  </span>
                  <span className="text-right tabular-nums text-token-secondary">{i.reorder_point || '—'}</span>
                  <span className="text-right tabular-nums text-token-secondary">{money(i.unit_cost)}</span>
                  <span className="text-right tabular-nums font-bold text-token-primary">{money(i.qty_on_hand * i.unit_cost)}</span>
                  <span className="flex justify-end gap-1">
                    <button aria-label="Restock" title="Restock" className="rounded-[7px] p-1.5 text-token-secondary hover:bg-canvas-subtle hover:text-token-primary" onClick={() => setRestock({ item: i, qty: 0 })}><PackagePlus className="h-[17px] w-[17px]" /></button>
                    <button aria-label="Edit" title="Edit" className="rounded-[7px] p-1.5 text-token-secondary hover:bg-canvas-subtle hover:text-token-primary" onClick={() => setEditing({ ...i })}><Pencil className="h-[17px] w-[17px]" /></button>
                    <button aria-label="Archive" title="Archive" className="rounded-[7px] p-1.5 text-token-secondary hover:bg-token-danger-surface hover:text-token-danger" onClick={() => { if (confirm(`Archive ${i.name}?`)) remove.mutate(i.id) }}><Trash2 className="h-[17px] w-[17px]" /></button>
                  </span>
                </Trow>
              )
            })}
          </Table>
        )}
      </div>

      {/* add / edit */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit item' : 'New inventory item'}
        footer={<>
          <button className={btnGhost} onClick={() => setEditing(null)}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save item'}</button>
        </>}
      >
        {editing && (
          <div className="space-y-3">
            <Field label="Name"><Input value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Engine oil 5W-30" autoFocus /></Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Category"><Input value={editing.category ?? ''} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="e.g. Fluids" /></Field>
              <Field label="SKU (optional)"><Input value={editing.sku ?? ''} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Unit"><Select value={editing.unit ?? 'unit'} onChange={(e) => setEditing({ ...editing, unit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</Select></Field>
              <Field label="Unit cost (AUD)"><Input type="number" min="0" step="0.01" value={editing.unit_cost ?? 0} onChange={(e) => setEditing({ ...editing, unit_cost: Number(e.target.value) })} /></Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Reorder point"><Input type="number" min="0" step="1" value={editing.reorder_point ?? 0} onChange={(e) => setEditing({ ...editing, reorder_point: Number(e.target.value) })} /></Field>
              {!editing.id && <Field label="Opening stock"><Input type="number" min="0" step="1" value={editing.qty_on_hand ?? 0} onChange={(e) => setEditing({ ...editing, qty_on_hand: Number(e.target.value) })} /></Field>}
            </div>
            {editing.id && <p className="text-[12.5px] text-token-secondary">To change stock on hand, use <strong>Restock</strong> so the change is recorded in the movement history.</p>}
          </div>
        )}
      </Modal>

      {/* restock */}
      <Modal
        open={!!restock}
        onClose={() => setRestock(null)}
        title={`Restock ${restock?.item.name ?? ''}`}
        footer={<>
          <button className={btnGhost} onClick={() => setRestock(null)}>Cancel</button>
          <button className={btnPrimary} onClick={doRestock} disabled={adjust.isPending || !restock?.qty}>{adjust.isPending ? 'Saving…' : 'Add stock'}</button>
        </>}
      >
        {restock && (
          <div className="space-y-3">
            <p className="text-[13.5px] text-token-secondary">On hand now: <strong className="text-token-primary">{restock.item.qty_on_hand} {restock.item.unit}</strong></p>
            <Field label={`Quantity to add (${restock.item.unit})`}><Input type="number" min="0" step="1" autoFocus value={restock.qty || ''} onChange={(e) => setRestock({ ...restock, qty: Number(e.target.value) })} /></Field>
            <p className="text-[12.5px] text-token-secondary">To correct a count downward, enter a negative number.</p>
          </div>
        )}
      </Modal>
          <Modal open={packOpen} onClose={() => setPackOpen(false)} title="Inventory starter pack"
        footer={<><button className={btnSecondary} onClick={() => setPackOpen(false)}>Cancel</button><button className={btnPrimary} onClick={applyPack} disabled={importMany.isPending}>{importMany.isPending ? 'Adding…' : 'Add items'}</button></>}>
        <div className="space-y-4">
          <Field label="Trade">
            <Select value={packId} onChange={(e) => setPackId(e.target.value)}>
              {INVENTORY_PACKS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </Select>
          </Field>
          <p className="text-[13px] text-token-secondary">
            Adds {INVENTORY_PACKS.find((p) => p.id === packId)?.items.length ?? 0} common items with sensible
            reorder points. Quantities start at zero and costs at zero, so do a stocktake and set your own
            buy prices. Nothing here appears on a customer invoice.
          </p>
          <div className="max-h-56 overflow-auto rounded-xl border border-token-subtle p-3 text-[12.5px] text-token-secondary">
            {(INVENTORY_PACKS.find((p) => p.id === packId)?.items ?? []).map((it) => (
              <div key={it.name} className="flex justify-between py-0.5">
                <span>{it.name}</span>
                <span className="text-token-secondary">reorder at {it.reorder} {it.unit}</span>
              </div>
            ))}
          </div>
        </div>
      </Modal>

</Page>
  )
}
